import { Dispatch } from 'react';
import { Actions, UpdateActions } from '../../reducer/TypeDefs';
import { EventMapDetails, FlowItemDataMap, LoaderHook, NotifyDialogStatus, ToastMessageHook, TranslateHook } from '../TypeDefs';
import { deleteCompensatoryEventMap } from './CemHelpers';
import { deleteConnector } from './ConnectorsHelpers';
import { deleteItem, deleteItemAndRefreshAssociatedServices } from './FlowItemsHelpers';
import { deleteStep, deleteSubsequentStep } from './JourneyStepHelpers';

/**
 * @method confirmDelete
 * @description Handles the delete confirmation action based on the current notify dialog status.
 * @param {LoaderHook} loader - The loader hook for displaying loading state.
 * @param {ToastMessageHook} toastMessage - The toast message hook for displaying toast messages.
 * @param {TranslateHook} translate - The translate hook for translating text.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 */
export const confirmDelete = (
  {
    loader,
    toastMessage,
    translate,
    dispatch,
    notifyDialogStatus,
    flowItemDataMap,
    cemRootNodeId,
    journeyName,
    journeyStepName,
    journeyVersion,
    deletingNodeId,
    designingCEM
  }:{
    loader: LoaderHook,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    notifyDialogStatus: NotifyDialogStatus | undefined,
    flowItemDataMap: FlowItemDataMap,
    cemRootNodeId: string | undefined
    journeyName: string,
    journeyStepName: string,
    journeyVersion: number,
    deletingNodeId: string | undefined,
    designingCEM: boolean
  }
  ) => {
  switch (notifyDialogStatus) {
    case NotifyDialogStatus.DELETE_CEM:
      deleteCompensatoryEventMap({
        loader,
        toastMessage,
        translate,
        dispatch,
        flowItemDataMap,
        cemRootNodeId,
        journeyName,
        journeyStepName,
        journeyVersion
      });
      break;
    case NotifyDialogStatus.DELETE_NODE:
      if (designingCEM) {
        deleteItemAndRefreshAssociatedServices(dispatch, deletingNodeId, flowItemDataMap, cemRootNodeId, designingCEM);
      } else {
        deleteItem(dispatch, deletingNodeId);
      }
      break;
    case NotifyDialogStatus.DELETE_STEP:
      deleteStep(dispatch, deletingNodeId);
      break;
    case NotifyDialogStatus.DELETE_SUBSEQUENT_STEP:
      deleteSubsequentStep(dispatch, deletingNodeId);
      break;
    default:
      deleteConnector(dispatch);
      break;
  }
};

/**
 * @method hideDeleteNotifyDialogHandler
 * @description Hides the delete confirmation dialogue after an action.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const hideDeleteNotifyDialogHandler = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.HIDE_DELETE_NOTIFY_DIALOG });
};

/**
 * @method confirmErrorHandler
 * @description Handles the confirmation of an error.
 * @param {() => void} goToPrevPage - The function to navigate to the previous page.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const confirmErrorHandler = (goToPrevPage: () => void, dispatch: Dispatch<UpdateActions>) => {
  goToPrevPage();
  dispatch({ type: Actions.HIDE_API_ERROR });
};

/**
 * @method proceedActiveStatusEditWarningModal
 * @description Proceeds with the active status edit warning modal.
 * @param {(arg: boolean) => void} setShowModalForActiveEdit - The function to set the show modal state for active edit.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const proceedActiveStatusEditWarningModal = (setShowModalForActiveEdit: (arg: boolean) => void, dispatch: Dispatch<UpdateActions>) => {
  setShowModalForActiveEdit(false);
  dispatch({
    type: Actions.VIEW_JOURNEY_INITIATED,
    payload: {
      flag: false,
    }
  });
};

/**
 * @method cancelActiveStatusEditWarningModal
 * @description Cancels the active status edit warning modal.
 * @param {(arg: boolean) => void} setShowModalForActiveEdit - The function to set the show modal state for active edit.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const cancelActiveStatusEditWarningModal = (setShowModalForActiveEdit: (arg: boolean) => void, dispatch: Dispatch<UpdateActions>) => {
  setShowModalForActiveEdit(false);
  dispatch({
    type: Actions.VIEW_JOURNEY_INITIATED,
    payload: {
      flag: true,
    }
  });
};

/**
 * @method leaveCancelWarningModal
 * @description Leaves the cancel warning modal.
 * @param {(arg: boolean) => void} setShowCancelWarningModal - The function to set the show cancel warning modal state.
 * @param {React.MutableRefObject<EventMapDetails | undefined>} stepDataRef - The mutable ref object for step data.
 * @param {() => void} goToPrevPage - The function to navigate to the previous page.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 */
export const leaveCancelWarningModal = (
  {
    setShowCancelWarningModal,
    stepDataRef,
    goToPrevPage,
    dispatch,
    designingStep
  }:{
    setShowCancelWarningModal: (arg: boolean) => void,
    stepDataRef: React.MutableRefObject<EventMapDetails | undefined>,
    goToPrevPage: () => void,
    dispatch: Dispatch<UpdateActions>,
    designingStep: boolean
  }
  ) => {
  setShowCancelWarningModal(false);
  if (designingStep) {
    stepDataRef.current = undefined;
    dispatch({ type: Actions.CANCEL_STEP_DESIGN });
  } else {
    goToPrevPage();
  }
};
